/**
 * RetireConfirmModal
 *
 * Confirms retirement of a focus area and collects a reason (required).
 * Reasons are free-form in Phase 1; Phase 4 may suggest canned options.
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface RetireConfirmModalProps {
  open: boolean;
  focusAreaName: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void> | void;
}

export function RetireConfirmModal({
  open,
  focusAreaName,
  onOpenChange,
  onConfirm,
}: RetireConfirmModalProps) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const handleConfirm = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Retire &ldquo;{focusAreaName}&rdquo;?</DialogTitle>
          <DialogDescription>
            Retired focus areas move to Archive. You can always add a new one
            later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="retire-reason">Reason</Label>
          <Textarea
            id="retire-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Resolved, not relevant anymore, etc."
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || !reason.trim()}
          >
            Retire
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
